/* eslint-disable */
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  ViewEncapsulation,
  WritableSignal,
} from "@angular/core";
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {
  concat,
  debounceTime,
  distinctUntilChanged,
  Observable,
  of,
  Subject,
} from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { getControlName } from "@shared/utils/getControlName";
import { CabGetRequest } from "@shared/requests/CabRequest";
import { DefaultService } from "@core/services/default.service";
import { BasicFormInputComponent } from "../../basic/basic-form-input.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateModule } from "@ngx-translate/core";
import { NgSelectModule } from "@ng-select/ng-select";
import { CabLabelComponent } from "../cab-label/cab-label.component";
import { AsyncPipe } from "@angular/common";

@UntilDestroy()
@Component({
  selector: "app-cab-autocomplete",
  templateUrl: "./cab-autocomplete.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CabLabelComponent,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    TranslateModule,
  ],
})
export class CabAutocompleteComponent
  extends BasicFormInputComponent
  implements OnChanges
{
  @Input({ required: true }) _defaultService: DefaultService<any, any, any>;
  @Input() width = "100%";
  @Input() readonly = false;
  @Input() clearable = true;
  @Input() bindLabel = "name";
  @Input() isConcatId = false;
  @Input() bindValue = "id";
  @Input() searchKey = "name";
  @Input() responseDataKey = "content";
  @Input() saveSelectedItemInLocalStorageAs: string;
  @Input() parentControl: AbstractControl | null;
  @Input() parentFilterName: string | null;
  @Input() firstSelect = false;
  @Input() firstSelectForName = false;
  @Input() firstLoad = false;
  @Input() showArrow = false;
  @Input() multiple = false;
  @Input() notFoundText = "SELECT.NOT_FOUND";
  @Input() params: any = {};

  @Output() onChange = new EventEmitter<any>();
  @Output() onClear = new EventEmitter<any>();

  loading: WritableSignal<boolean> = signal(false);
  items$: Observable<any>;
  searchInput$: Subject<string> = new Subject<string>();
  public _request: CabGetRequest;
  private _http = inject(HttpClient);

  ngOnChanges(params: any) {
    if (params?.params && this._request) {
      this._request?.setDefaultParams({
        ...this._request.defaultParams,
        ...this.params,
        size: 1000,
      });

      this.loadData();
    }
  }

  ngOnInit() {
    super.ngOnInit();
    this._request = new CabGetRequest(this._defaultService._api);
    this._request.setDefaultParams({
      ...this._request.defaultParams,
      ...this.params,
      size: 1000,
    });
    if (this.parentControl) {
      this.parentFilterName ||= getControlName(this.parentControl);

      this.parentControl.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.clearData();
        });
    }

    this.loadData();
    this.onSearch();
  }

  clearData(event?: any) {
    if (this.formControl.value !== null) this.formControl.patchValue(null);
    this.onClear.emit(event);
    this.items$ = of([]);
    this._request.resetParams();
    this.loadData();
  }

  onSearch() {
    this.searchInput$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        map((term) => {
          this._request.putParam(this.searchKey, term);
          this.loadData();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  loadData() {
    if (this.parentControl) {
      if (this.parentControl.value === null) {
        return;
      } else {
        this._request.putParam(
          String(this.parentFilterName),
          this.parentControl.value
        );
      }
    }
    this.loading.set(true);

    this.items$ = concat(
      of([]),
      this._http
        .get(this._request.getUrl(), {
          params: this._request.getParams(),
        })
        .pipe(
          map((res: any) => {
            if (this.firstSelect && !this.formControl?.value) {
              const first = res[this.responseDataKey]
                ? res[this.responseDataKey][0]
                : res[0];
              this.formControl.setValue(first.id ?? this.id);

              this.onChangeSelect(first);
            }

            if (this.isConcatId) {
              const originalBindLabel = this.bindLabel;
              (res.content ?? res).forEach((item: any) => {
                item[this.bindLabel] =
                  `${item?.id + " - " + item[originalBindLabel]}`;
              });
            }

            return res[this.responseDataKey] ?? res;
          }),
          catchError(() => of([])),
          finalize(() => this.loading.set(false))
        )
    );
  }

  onChangeSelect(item: any) {
    if (this.saveSelectedItemInLocalStorageAs) {
      localStorage.setItem(
        this.saveSelectedItemInLocalStorageAs,
        JSON.stringify(item)
      );
    }

    this.onChange.emit(item);
  }

  isReadonly(): boolean {
    if (this.readonly) return true;
    if (this.parentControl) {
      return this.parentControl.value == null;
    }

    return false;
  }
}
